import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExpandMore } from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import closeIcon from "@iconify/icons-mdi/close";
import addIcon from "@iconify/icons-mdi/plus-circle-outline";
import searchIcon from "@iconify/icons-mdi/search";
import { InlineIcon } from "@iconify/react";
import { useOcProperty } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

const useStyles = makeStyles((theme: Theme) => ({
  basicBoxCss: {
    border: `1px solid ${theme.palette.gray40.main}`,
    borderRadius: "4px",
    color: theme.palette.darkBase.main,
  },
  boxError: {
    border: `1px solid ${theme.palette.primary.main} !important`,
    borderRadius: "4px",
    color: theme.palette.darkBase.main,
  },
}));

interface OcFormEditorItemListProps {
  search: string;
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onScroll: () => void;
  value: any;
  name: string;
  label: string;
  onClearSearch: () => void;
  list: Array<{ code: string; name: string; validTo?: string | null }>;
  onChange: (name: string, value: any) => void;
  error: boolean;
  showCode?: boolean;
  showItem?: boolean;
  module?: string;
  addNewProject?: () => void;
}

export const OcFormEditorItemList: React.FC<OcFormEditorItemListProps> = ({
  search,
  onSearch,
  onScroll,
  value,
  name,
  label,
  onClearSearch,
  list,
  onChange,
  error,
  showCode = false,
  showItem = false,
  module,
  addNewProject,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useOcIsMobile();

  const [expanded, setExpanded] = useState(false);

  const paddingTop = module === "MyTimesheet" ? "18px" : "36px";

  const filteredList =
    module === "MyTimesheet"
      ? list.filter((item) => item.validTo === null)
      : list;

  const handleExpanded = () => {
    setExpanded(!expanded);
  };

  const observer = useRef(null);

  const handleScroll = () => {
    if (observer.current) {
      const { scrollTop, scrollHeight, clientHeight } = observer.current as any;
      if (scrollTop + clientHeight === scrollHeight) {
        onScroll();
      }
    }
  };

  const handleChange = (item: { code: string; name: string }) => {
    setExpanded(false);
    let valueToSet = item?.code;
    if (showItem) {
      valueToSet = item.code;
    }
    onChange(name, valueToSet);
  };

  let parsedLabel = t(label);
  if (value) {
    parsedLabel = showCode ? value?.code : value?.name;
  }

  const { data: saveNewProjectByUserEnabled } = useOcProperty({
    resource: "myTimeSheet",
    key: "saveNewProjectByUserEnabled",
  });

  const newProjectEnabled =
    saveNewProjectByUserEnabled === "false" ? false : true;

  return (
    <Box paddingTop={paddingTop} width={isMobile ? "100%" : "auto"}>
      <Collapse in={value ? true : false}>
        <Box
          style={{
            color: theme.palette.gray80.main,
            background: "white",
            fontSize: "12px",
            margin: "-7px 0px 0px 9px",
            padding: "0px 8px",
            position: "absolute",
          }}
        >
          {t(label)}
        </Box>
      </Collapse>

      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        width={isMobile ? "100%" : module === "MyTimesheet" ? "552px" : "536px"}
        height="56px"
        paddingLeft={isMobile ? "0px" : "14px"}
        marginLeft={isMobile ? "-1px" : "0px"}
        className={error ? classes.boxError : classes.basicBoxCss}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          paddingLeft={isMobile ? "14px" : "0px"}
          color={
            value !== undefined
              ? `${theme.palette.darkBase.main}`
              : error === true
                ? `${theme.palette.primary.main}`
                : `${theme.palette.gray80.main}`
          }
        >
          <Box fontSize="16px">{parsedLabel}</Box>
        </Box>
        <Box paddingRight="28px">
          <IconButton
            id={`${name}`}
            onClick={handleExpanded}
            aria-expanded={expanded}
            aria-label="show more"
            style={
              expanded
                ? {
                  padding: 0,
                  transform: "rotate(180deg)",
                }
                : { padding: 0 }
            }
          >
            <ExpandMore />
          </IconButton>
        </Box>
      </Box>
      {module === "MyTimesheet" && !expanded && (
        <Box
          style={{
            fontSize: "12px",
            color: "#808080",
            marginLeft: "15px",
          }}
        >
          {t("OC_OPTIONAL")}
        </Box>
      )}
      <Collapse in={expanded}>
        <Box
          padding="16px"
          style={{
            backgroundColor: theme.palette.gray30.main,
            borderRadius: "4px",
          }}
        >
          <Box
            style={{
              backgroundColor: "white",
              borderRadius: "4px",
              height: "48px",
            }}
          >
            <TextField
              id={`search-${name}`}
              label=""
              variant="outlined"
              fullWidth
              value={search}
              onChange={onSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InlineIcon icon={searchIcon} color="#919699" height={20} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon
                      id={`close-${label}`}
                      icon={closeIcon}
                      onClick={onClearSearch}
                      color="#919699"
                      style={{ cursor: "pointer" }}
                    />
                  </InputAdornment>
                ),
                style: { height: "48px" },
              }}
            />
          </Box>
          <Box height="16px" />
          <Box
            ref={observer}
            onScroll={handleScroll}
            style={{
              backgroundColor: "white",
              fontSize: "16px",
              letterSpacing: "0.44px",
              lineHeight: "24px",
              padding: "8px 8px",
              maxHeight: "180px",
              overflowY: "auto",
              color: theme.palette.darkBase.main,
              border: "1px solid #C6CACC",
            }}
          >
            {filteredList.length === 0 ? (
              <Box style={{ color: theme.palette.gray80.main, padding: "8px" }}>
                {t("OC_NO_RESULTS")}
              </Box>
            ) : (
              filteredList.map((item, idx) => {
                let displayText;

                if (isMobile && item?.name?.length > 15 && !showCode) {
                  displayText = `${item?.name.substring(0, 15)}...`;
                } else if (showCode) {
                  displayText = item?.code;
                } else {
                  displayText = item?.name;
                }
                return (
                  <Box key={`${item.code}-${idx}`} onClick={() => handleChange(item)}>
                    <MenuItem id={item?.code} value={item?.code}>
                      {displayText}
                    </MenuItem>
                  </Box>
                );
              })
            )}
          </Box>
          {name === "project" && newProjectEnabled && (
            <>
              <Box height="16px" />
              <Box display="flex" style={{ cursor: "pointer" }} onClick={addNewProject}>
                <InlineIcon id="add-projct" width="24px" icon={addIcon} />
                <Box width="16px" />
                <Typography>{t("MY_TIMESHEET_ADD_NEW_PROJECT")}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
      {module === "MyTimesheet" && expanded && <Box height="14px" />}
    </Box>
  );
};

export default OcFormEditorItemList;
